import { useT } from "@magic-md/i18n/react";
import { CheckCircle2, ChevronRight, Loader2, XCircle } from "lucide-react";
import { cn } from "../../../lib/utils";
import { getThemeAccentStyle } from "../../../theme/runtime/theme-accents";

export type CollapsedUnitRenderStatus = "error" | "running" | "success" | null;

export interface CollapsedPluginRenderManifest {
    name: string;
    version: string;
    title?: string;
}

export function getPluginDisplayName(manifest: CollapsedPluginRenderManifest): string {
    const title = manifest.title?.trim();
    return title || manifest.name;
}

export function getPluginTechnicalLabel(manifest: CollapsedPluginRenderManifest): string {
    return `${manifest.name}@${manifest.version}`;
}

function CollapsedStatusIcon({
    status,
    labels,
}: {
    status: CollapsedUnitRenderStatus;
    labels: {
        error: string;
        running: string;
        success: string;
    };
}) {
    if (status === "error") {
        return (
            <span className="inline-flex shrink-0" title={labels.error}>
                <XCircle
                    size={13}
                    className="theme-accent-text"
                    style={getThemeAccentStyle("error")}
                    aria-label={labels.error}
                />
            </span>
        );
    }

    if (status === "running") {
        return (
            <span className="inline-flex shrink-0" title={labels.running}>
                <Loader2
                    size={13}
                    className="animate-spin theme-accent-text"
                    style={getThemeAccentStyle("running")}
                    aria-label={labels.running}
                />
            </span>
        );
    }

    if (status === "success") {
        return (
            <span className="inline-flex shrink-0" title={labels.success}>
                <CheckCircle2
                    size={13}
                    className="theme-accent-text"
                    style={getThemeAccentStyle("success")}
                    aria-label={labels.success}
                />
            </span>
        );
    }

    return null;
}

export function CollapsedUnitRenderHeader({
    unitId,
    label,
    technicalLabel,
    status,
    statusLabels,
    onExpand,
}: {
    unitId: string;
    label: string;
    technicalLabel?: string;
    status: CollapsedUnitRenderStatus;
    statusLabels?: Partial<Record<Exclude<CollapsedUnitRenderStatus, null>, string>>;
    onExpand: () => void;
}) {
    const t = useT("editor");
    const labels = {
        error: statusLabels?.error ?? t("render.status.failed"),
        running: statusLabels?.running ?? t("status.running"),
        success: statusLabels?.success ?? t("render.status.ran"),
    };
    return (
        <div
            className="my-1.5 overflow-hidden rounded-lg border border-border bg-background"
            data-render-collapsed="true"
        >
            <button
                type="button"
                className={cn(
                    "flex w-full min-w-0 items-center gap-2 border-none bg-muted/30 px-3 py-2 text-left text-[11px]",
                    "transition-colors hover:bg-muted/40 hover:text-foreground",
                )}
                onClick={onExpand}
                aria-label={t("render.expandUnit", { id: unitId })}
                aria-expanded="false"
                title={t("common.expand")}
            >
                <CollapsedStatusIcon status={status} labels={labels} />
                <span className="min-w-0 flex-1 truncate font-mono text-foreground/80">
                    {unitId}
                </span>
                <span
                    className="min-w-0 max-w-[46%] shrink truncate text-right text-muted-foreground/60"
                    title={technicalLabel}
                >
                    {label}
                </span>
                <ChevronRight
                    size={14}
                    className="shrink-0 text-muted-foreground/70"
                    aria-hidden="true"
                />
            </button>
        </div>
    );
}
